import { LinkedinFilled } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import {
  Company,
  CompanyClaim,
  GET_COMPANY,
  GET_COMPANY_CLAIM_REQUESTS,
  GET_ME,
  GET_REGIONS,
  KeyPerson,
  LIST_KEY_PERSON,
  Me,
  Region,
} from '@frontend/data-access';
import { getLinkLabel } from '@frontend/shared/util';
import { Button, Col, Modal, Row, Typography } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useMemo, useState } from 'react';

import UserAvatar from '../../user-avatar/user-avatar';
import EditIndustriesForm from '../components/edit-actions/industries';
import EditInformationDeckForm from '../components/edit-actions/info-deck-form';
import EditKeyPersonForm from '../components/edit-actions/key-person';
import EditPitchForm from '../components/edit-actions/pitch';
import EditProfileManagerForm from '../components/edit-actions/profile-manager';
import EditRegionForm from '../components/edit-actions/region';
import EditShortDescForm from '../components/edit-actions/short-description';
import EditInformationCard, {
  EditInformationCardProps,
} from '../components/edit-info-card';
import InformationDeck from '../components/information-deck';
import PitchVideo from '../components/pitch-video';
import TagItems from '../components/tag-item';

type ActionTypes =
  | 'ABOUT'
  | 'INDUSTRIES'
  | 'REGION'
  | 'KEY_PERSON'
  | 'PROFILE_MANAGERS'
  | 'INFORMATION_DECK'
  | 'PITCH_VIDEO';

const { Text } = Typography;

const About = () => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const id = Number(router.query['companyId']);
  const [actionType, setActionType] = useState<ActionTypes>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const { data } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: id },
  });

  const { data: dataRegions } = useQuery<{
    regions: Region[];
  }>(GET_REGIONS);

  const { data: dataKeyPerson } = useQuery<{
    listKeyPerson: KeyPerson[];
  }>(LIST_KEY_PERSON, {
    variables: {
      companyId: {
        companyId: id,
      },
    },
  });

  const { data: listClaimRequest } = useQuery<{
    listCompanyClaims: CompanyClaim[];
  }>(GET_COMPANY_CLAIM_REQUESTS, {
    variables: { companyId: id },
  });

  const pitches = useMemo(() => {
    return data?.company?.pitches?.filter((item) => !!item) ?? [];
  }, [data?.company?.pitches]);

  const renderTitle = () => {
    return t('edit_title', {
      title: t(actionType?.toLowerCase() as string).replace('_', ' '),
    });
  };

  const handleClose = () => {
    setActionType(undefined);
    setIsModalOpen(false);
  };

  const renderAction = () => {
    if (data)
      switch (actionType) {
        case 'ABOUT':
          return (
            <EditShortDescForm
              companyId={data?.company.id}
              onCancel={handleClose}
              onFinish={handleClose}
            />
          );
        case 'INDUSTRIES':
          return (
            <EditIndustriesForm
              companyId={data?.company.id}
              onCancel={handleClose}
              onFinish={handleClose}
            />
          );

        case 'REGION':
          return (
            <EditRegionForm
              companyId={data?.company.id}
              onCancel={handleClose}
              onFinish={handleClose}
            />
          );
        case 'KEY_PERSON':
          return (
            <EditKeyPersonForm
              companyId={data?.company.id}
              onCancel={handleClose}
              onFinish={handleClose}
            />
          );
        case 'PROFILE_MANAGERS':
          return (
            <EditProfileManagerForm
              companyId={data?.company.id}
              onCancel={handleClose}
            />
          );
        case 'INFORMATION_DECK':
          return (
            <EditInformationDeckForm
              companyId={data?.company.id}
              onCancel={handleClose}
              onFinish={handleClose}
            />
          );
        case 'PITCH_VIDEO':
          return (
            <EditPitchForm
              companyId={data?.company.id}
              onCancel={handleClose}
              onFinish={handleClose}
            />
          );

        default:
          break;
      }
    return <div />;
  };

  const handleModal = (type: ActionTypes) => {
    setActionType(type);
    setIsModalOpen(true);
  };

  const items: EditInformationCardProps[] = useMemo(
    () => [
      {
        title: t('about'),
        content: <Text>{data?.company?.description}</Text>,
        extraAction: (
          <Button
            type="link"
            onClick={() => handleModal('ABOUT')}
            data-test="edit-about-btn"
          >
            {t('edit')}
          </Button>
        ),
      },
      {
        title: t('industries'),
        content:
          (data?.company.topics && data?.company.topics.length && (
            <TagItems data={data?.company.topics} test="selected-location" />
          )) ||
          null,
        extraAction: (
          <Button
            type="link"
            onClick={() => handleModal('INDUSTRIES')}
            data-test="edit-industries-btn"
          >
            {t('edit')}
          </Button>
        ),
      },
      {
        title: t('region'),
        content:
          (data?.company.market?.regionIds?.length && (
            <TagItems
              data={data?.company.market?.regionIds.map((i) => ({
                name: dataRegions?.regions.find((r) => r.id === i)?.name ?? '',
              }))}
              test="selected-regions"
            />
          )) ||
          null,
        extraAction: (
          <Button
            type="link"
            onClick={() => handleModal('REGION')}
            data-test="edit-region-btn"
          >
            {t('edit')}
          </Button>
        ),
      },
      {
        title: t('key_persons'),
        content:
          (dataKeyPerson?.listKeyPerson.length &&
            dataKeyPerson.listKeyPerson.length > 0 && (
              <Row className="gap-2 mt-1">
                {dataKeyPerson.listKeyPerson.map((item, index) => {
                  return (
                    <Col
                      className="flex flex-row gap-5 items-center bg-gray-200 py-3 pl-6 pr-3 rounded-full text-xs"
                      key={index}
                    >
                      <div>
                        <div className="text-gray-900 font-semibold">
                          {item.name}
                        </div>
                        <span className="text-gray-700 font-normal text-xs">
                          {item.role}
                        </span>
                      </div>
                      {item.linkedinUrl && (
                        <Link href={item.linkedinUrl} target="_blank">
                          <Button shape="circle" icon={<LinkedinFilled />} />
                        </Link>
                      )}
                    </Col>
                  );
                })}
              </Row>
            )) ||
          null,
        extraAction: (
          <Button
            type="link"
            onClick={() => handleModal('KEY_PERSON')}
            data-test="edit-key-person-btn"
          >
            {t('edit')}
          </Button>
        ),
      },
      {
        title: t('institution_profile_managers'),
        content:
          (data?.company.owners && data?.company.owners.length > 0 && (
            <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
              {data?.company?.owners.map((item, index) => (
                <div
                  className="flex items-center gap-4 px-4 py-[14px] bg-gray-200 rounded-full w-full"
                  key={index}
                >
                  <UserAvatar
                    size={40}
                    user={item.user}
                    showBadge={meData?.me.role !== 'EXTERNAL_USER'}
                  />
                  <div>
                    <div className="text-gray-900 font-medium text-sm">
                      {item.user.fullName}
                    </div>
                    <div className="text-gray-700 font-normal text-xs">
                      {item.user.role &&
                        getLinkLabel(item.user.role.toLowerCase())}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )) ||
          null,
        extraAction: (
          <>
            {listClaimRequest?.listCompanyClaims?.length !== undefined &&
              listClaimRequest?.listCompanyClaims?.length > 0 && (
                <span className="text-gray-700">{`(${
                  listClaimRequest?.listCompanyClaims?.filter(
                    (i) => i.status === 'PENDING'
                  ).length
                } pending) `}</span>
              )}
            <Button
              type="link"
              onClick={() => handleModal('PROFILE_MANAGERS')}
              data-test="edit-profile-mangers-btn"
            >
              {t('edit')}
            </Button>
          </>
        ),
        hasAccess: meData?.me?.role === 'ADMIN',
      },
      {
        title: t('information_deck'),
        content: (
          <InformationDeck
            data={data?.company?.onePagers}
            companyId={data?.company.id}
          />
        ),
        extraAction: data?.company?.onePagers &&
          data?.company?.onePagers?.length > 0 && (
            <Button
              type="link"
              onClick={() => handleModal('INFORMATION_DECK')}
              data-test="edit-info-desk-btn"
            >
              {t('upload_file')}
            </Button>
          ),
      },
      {
        title: t('pitch_video'),
        content: (
          <PitchVideo
            data={data?.company?.pitchVideos ?? []}
            pitches={pitches}
            companyId={data?.company.id}
          />
        ),
        extraAction: (data?.company?.pitchVideos?.length !== 0 ||
          pitches.length !== 0) &&
          data?.company?.isOwner && (
            <Button
              type="link"
              onClick={() => handleModal('PITCH_VIDEO')}
              data-test="edit-pitch-video-btn"
            >
              {t('upload_file')}
            </Button>
          ),
      },
    ],
    [
      data?.company,
      dataKeyPerson?.listKeyPerson,
      dataRegions?.regions,
      listClaimRequest?.listCompanyClaims,
      meData?.me.role,
      pitches,
      t,
    ]
  );

  return (
    <div>
      <Col className="space-y-6">
        {items.map((item, index) => (
          <EditInformationCard
            key={index}
            title={item.title}
            extraAction={
              data?.company.isOwner || item.hasAccess ? item.extraAction : null
            }
            content={item.content}
          />
        ))}
      </Col>
      <Modal
        title={renderTitle()}
        open={isModalOpen}
        onCancel={handleClose}
        width={800}
        footer={false}
      >
        {renderAction()}
      </Modal>
    </div>
  );
};

export default About;
